import {
  PBIS_CSV_IMPORT_WIRE_EVENTS,
  getPbisPointsWireEventNames
} from "@/constants/behaviourPbisSocketConstants.js"
import { useToast } from "vue-toastification"

/** @type {Set<(studentIds: number[]) => void>} */
const refreshHandlers = new Set()

/** @type {Set<() => void>} */
const csvUploadRefreshHandlers = new Set()

/**
 * Collapse rapid PbisPoints* socket storms (e.g. bulk CSV) into one refetch
 * of history/summary APIs instead of calling mine + transactions per event.
 */
const PBIS_POINTS_REFRESH_DEBOUNCE_MS = 1500

/** Collapse ManualImportLogUpdateEvent status spam for Upload log. */
const PBIS_CSV_UPLOAD_REFRESH_DEBOUNCE_MS = 1500

/** Dedupe completion toasts when the same event arrives on adult + admin. */
const recentCsvCompletionKeys = new Set()
const CSV_COMPLETION_DEDUPE_MS = 8000

/** After a sync CSV form toast, suppress near-immediate socket completion toasts. */
let suppressCompletionToastUntil = 0

/** @type {ReturnType<typeof setTimeout> | null} */
let pointsRefreshTimer = null
/** @type {import('vuex').Store | null} */
let pendingPointsRefreshStore = null
/** @type {Set<number>} */
let pendingPointsRefreshStudentIds = new Set()

/** @type {ReturnType<typeof setTimeout> | null} */
let csvUploadRefreshTimer = null

/**
 * @param {(studentIds: number[]) => void} handler
 * @returns {() => void}
 */
export function registerPbisPointsRefreshHandler(handler) {
  if (typeof handler !== "function") {
    return () => {}
  }

  refreshHandlers.add(handler)
  return () => refreshHandlers.delete(handler)
}

/**
 * @param {unknown} channelName
 * @returns {string}
 */
export function normalizePbisPointsChannelName(channelName) {
  if (typeof channelName !== "string" || !channelName) {
    return ""
  }

  return channelName.startsWith("private-")
    ? channelName
    : `private-${channelName}`
}

/**
 * @param {unknown} payload
 * @returns {Record<string, unknown>}
 */
export function normalizePbisPointsMutationPayload(payload) {
  if (typeof payload === "string") {
    try {
      const parsed = JSON.parse(payload)
      return typeof parsed === "object" && parsed != null ? parsed : {}
    } catch {
      return {}
    }
  }

  if (payload && typeof payload === "object") {
    const data = payload?.data
    if (
      data &&
      typeof data === "object" &&
      (data?.student_ids != null ||
        data?.studentIds != null ||
        data?.mutation_type != null)
    ) {
      return data
    }
    return payload
  }

  return {}
}

/**
 * @param {unknown} payload
 * @returns {boolean}
 */
export function isPbisPointsMutationPayload(payload) {
  const normalized = normalizePbisPointsMutationPayload(payload)
  return Boolean(normalized?.mutation_type)
}

/**
 * @param {unknown} payload
 * @returns {number[]}
 */
export function normalizePbisPointsMutationStudentIds(payload) {
  const normalized = normalizePbisPointsMutationPayload(payload)
  const raw = normalized?.student_ids ?? normalized?.studentIds ?? []
  if (!Array.isArray(raw)) {
    return []
  }
  return raw.map((id) => Number(id)).filter((id) => Number.isFinite(id))
}

/**
 * @param {number[]} studentIds
 * @returns {number[]}
 */
function mergePendingStudentIds(studentIds = []) {
  ;(Array.isArray(studentIds) ? studentIds : []).forEach((id) => {
    const parsed = Number(id)
    if (Number.isFinite(parsed)) {
      pendingPointsRefreshStudentIds.add(parsed)
    }
  })
  return [...pendingPointsRefreshStudentIds]
}

/**
 * Flush coalesced PBIS history/summary refreshes to mounted views.
 */
export function flushPbisPointsUiRefresh() {
  if (pointsRefreshTimer != null) {
    clearTimeout(pointsRefreshTimer)
    pointsRefreshTimer = null
  }

  const store = pendingPointsRefreshStore
  const studentIds = [...pendingPointsRefreshStudentIds]
  pendingPointsRefreshStore = null
  pendingPointsRefreshStudentIds = new Set()

  if (!store) {
    return
  }

  store.commit("behaviourManage/notifyPbisPointsMutated", { studentIds })

  refreshHandlers.forEach((handler) => {
    try {
      handler(studentIds)
    } catch {
      // Ignore handler failures so one view cannot break others.
    }
  })
}

export function schedulePbisPointsUiRefresh(
  store,
  studentIds = [],
  { immediate = false } = {}
) {
  if (!store) {
    return
  }

  pendingPointsRefreshStore = store
  mergePendingStudentIds(studentIds)

  if (immediate) {
    flushPbisPointsUiRefresh()
    return
  }

  if (pointsRefreshTimer != null) {
    clearTimeout(pointsRefreshTimer)
  }

  pointsRefreshTimer = setTimeout(() => {
    pointsRefreshTimer = null
    flushPbisPointsUiRefresh()
  }, PBIS_POINTS_REFRESH_DEBOUNCE_MS)
}

export function dispatchPbisPointsRefresh(store, payload) {
  if (!isPbisPointsMutationPayload(payload)) {
    return
  }

  schedulePbisPointsUiRefresh(
    store,
    normalizePbisPointsMutationStudentIds(payload)
  )
}

/**
 * @param {import('vuex').Store} store
 * @param {unknown} payload
 */
export function notifyPbisPointsMutated(store, payload) {
  dispatchPbisPointsRefresh(store, payload)
}

/**
 * @param {import('laravel-echo').default['private']} channel
 * @param {import('vuex').Store} store
 * @param {{ onMutation?: (payload: Record<string, unknown>) => void }} [options]
 */
export function attachPbisPointsSocketListeners(channel, store, options = {}) {
  if (!channel?.listen || !store) {
    return
  }

  const onMutation = (payload) => {
    dispatchPbisPointsRefresh(store, payload)
    options.onMutation?.(payload)
  }

  getPbisPointsWireEventNames().forEach((eventName) => {
    channel.listen(`.${eventName}`, onMutation)
  })
}

export function registerPbisCsvUploadRefreshHandler(handler) {
  if (typeof handler !== "function") {
    return () => {}
  }

  csvUploadRefreshHandlers.add(handler)
  return () => csvUploadRefreshHandlers.delete(handler)
}

/**
 * Flush coalesced Upload log refreshes.
 */
export function flushPbisCsvUploadRefresh() {
  if (csvUploadRefreshTimer != null) {
    clearTimeout(csvUploadRefreshTimer)
    csvUploadRefreshTimer = null
  }

  csvUploadRefreshHandlers.forEach((handler) => {
    try {
      handler()
    } catch {
      // Ignore handler failures so one view cannot break others.
    }
  })
}

export function dispatchPbisCsvUploadRefresh({ immediate = false } = {}) {
  if (immediate) {
    flushPbisCsvUploadRefresh()
    return
  }

  if (csvUploadRefreshTimer != null) {
    clearTimeout(csvUploadRefreshTimer)
  }

  csvUploadRefreshTimer = setTimeout(() => {
    csvUploadRefreshTimer = null
    flushPbisCsvUploadRefresh()
  }, PBIS_CSV_UPLOAD_REFRESH_DEBOUNCE_MS)
}

export function normalizePbisCsvImportPayload(payload) {
  if (typeof payload === "string") {
    try {
      const parsed = JSON.parse(payload)
      return typeof parsed === "object" && parsed != null ? parsed : {}
    } catch {
      return {}
    }
  }

  if (payload && typeof payload === "object") {
    return payload
  }

  return {}
}

function isCurrentUserCsvEvent(store, payload) {
  const currentUserId = Number(store?.state?.authentication?.user?.id)
  const eventUserId = Number(payload?.user_id ?? payload?.import_log?.user_id)

  if (!Number.isFinite(currentUserId) || !Number.isFinite(eventUserId)) {
    return true
  }

  return currentUserId === eventUserId
}

function isDuplicateCsvCompletion(key) {
  if (!key) {
    return false
  }

  if (recentCsvCompletionKeys.has(key)) {
    return true
  }

  recentCsvCompletionKeys.add(key)
  setTimeout(() => {
    recentCsvCompletionKeys.delete(key)
  }, CSV_COMPLETION_DEDUPE_MS)

  return false
}

export function claimPbisCsvCompletionToast(importLogId) {
  const id = Number(importLogId)
  const hasImportLogId = Number.isFinite(id)

  if (Date.now() < suppressCompletionToastUntil) {
    if (hasImportLogId) {
      isDuplicateCsvCompletion(`completion:${id}`)
    }
    return false
  }

  if (hasImportLogId && isDuplicateCsvCompletion(`completion:${id}`)) {
    return false
  }

  suppressCompletionToastUntil = Date.now() + CSV_COMPLETION_DEDUPE_MS
  return true
}

export function markPbisCsvSyncCompletionHandled(importLogId) {
  return claimPbisCsvCompletionToast(importLogId)
}

export function handleManualImportLogUpdate(store, rawPayload) {
  const payload = normalizePbisCsvImportPayload(rawPayload)
  if (!payload?.import_log && payload?.school_id == null) {
    return
  }

  dispatchPbisCsvUploadRefresh()
}

export function handlePbisPointsCsvCompleted(store, rawPayload) {
  const payload = normalizePbisCsvImportPayload(rawPayload)
  if (!payload || typeof payload !== "object") {
    return
  }

  // Final state — refresh Upload log immediately (skip status-storm debounce).
  dispatchPbisCsvUploadRefresh({ immediate: true })

  const status = String(payload?.status ?? "")
    .trim()
    .toLowerCase()
  const isSuccess = status === "success"
  const isFailed = status === "failed"

  if (!isSuccess && !isFailed) {
    return
  }

  if (!isCurrentUserCsvEvent(store, payload)) {
    return
  }

  const shouldToast = claimPbisCsvCompletionToast(payload?.import_log_id)

  if (shouldToast) {
    const message = String(payload?.message ?? "").trim()
    if (message) {
      const toast = useToast()
      if (isSuccess) {
        toast.success(message)
      } else {
        toast.error(message)
      }
    }
  }

  if (isSuccess && store) {
    // Flush any pending mutation-storm refresh into one history/summary refetch.
    schedulePbisPointsUiRefresh(store, [], { immediate: true })
  }
}

export function attachPbisCsvImportSocketListeners(channel, store) {
  if (!channel?.listen || !store) {
    return
  }

  channel.listen(`.${PBIS_CSV_IMPORT_WIRE_EVENTS.LOG_UPDATE}`, (payload) => {
    handleManualImportLogUpdate(store, payload)
  })

  channel.listen(`.${PBIS_CSV_IMPORT_WIRE_EVENTS.COMPLETED}`, (payload) => {
    handlePbisPointsCsvCompleted(store, payload)
  })
}
